/**
 * ADR 0014 -- a reaction composed on the drawing paper. A reaction is a directed graph: the
 * reactant set and the product set are its nodes, and the arrow is the edge, carrying reagents
 * and catalysts (drawn above it) and solvents (drawn below it). This module derives that graph
 * from where the molecules sit relative to the drawing's reaction arrow -- membership is by
 * position, so drawing, moving or deleting a molecule just re-derives it; nothing is stored.
 *
 * Pure and coordinate-space agnostic: the caller passes positions in any uniform space (paper
 * pixels for rendering the zones, document units for the save mapping in P3).
 */

export type ReactionZone = 'reactants' | 'products' | 'above' | 'below';
export const REACTION_ZONES: readonly ReactionZone[] = ['reactants', 'products', 'above', 'below'];

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface MoleculeComponent {
  readonly atomIds: readonly string[];
  readonly bounds: Rect;
}

export interface ReactionMolecule extends MoleculeComponent {
  readonly zone: ReactionZone;
}

export interface ReactionZoneBox {
  readonly rect: Rect;
  /** No molecule in it yet -- drawn as a placeholder beside the arrow. */
  readonly empty: boolean;
}

export interface ReactionLayout {
  readonly arrowId: string;
  readonly molecules: readonly ReactionMolecule[];
  readonly zones: Readonly<Record<ReactionZone, ReactionZoneBox>>;
}

export interface ReactionArrowGeometry {
  readonly id: string;
  readonly from: Point;
  readonly to: Point;
}

export interface ReactionLayoutOptions {
  /** Space between a zone's edge and its molecules. */
  readonly padding: number;
  /** Space between the arrow and an empty zone's placeholder. */
  readonly gap: number;
  /** Smallest half-size of an empty zone's placeholder. */
  readonly minPlaceholder: number;
}

/** The drawing's connected components (a lone atom is a molecule too), with their bounds. */
export function moleculeComponents(
  nodeIds: readonly string[],
  edges: readonly { readonly source: string; readonly target: string }[],
  position: (atomId: string) => Point | undefined
): MoleculeComponent[] {
  const adjacency = new Map<string, string[]>();
  for (const { source, target } of edges) {
    adjacency.set(source, [...(adjacency.get(source) ?? []), target]);
    adjacency.set(target, [...(adjacency.get(target) ?? []), source]);
  }
  const seen = new Set<string>();
  const components: MoleculeComponent[] = [];
  for (const start of nodeIds) {
    if (seen.has(start) || !position(start)) continue;
    const atomIds: string[] = [];
    const queue = [start];
    seen.add(start);
    while (queue.length) {
      const id = queue.shift()!;
      atomIds.push(id);
      for (const next of adjacency.get(id) ?? []) {
        if (!seen.has(next)) {
          seen.add(next);
          queue.push(next);
        }
      }
    }
    const points = atomIds.map(position).filter((point): point is Point => point !== undefined);
    components.push({ atomIds, bounds: boundsOf(points) });
  }
  return components;
}

/** The arrow's unit axis and the unit normal pointing to its "above" side: screen-up for a
 *  sideways arrow (y grows downward), and to the right for a vertical one -- where reagents go by
 *  convention. */
export function arrowFrame(from: Point, to: Point) {
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const axis = { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
  let normal = { x: axis.y, y: -axis.x };
  if (normal.y > 0 || (normal.y === 0 && normal.x < 0)) normal = { x: -normal.x, y: -normal.y };
  return { axis, normal, length };
}

/** Which zone a point (a molecule's bounding-box centre) falls in: behind the arrow's start ->
 *  reactants, past its end -> products, alongside it -> above or below by side. */
export function zoneOf(point: Point, from: Point, to: Point): ReactionZone {
  const { axis, normal, length } = arrowFrame(from, to);
  const offset = { x: point.x - from.x, y: point.y - from.y };
  const along = (offset.x * axis.x + offset.y * axis.y) / length;
  if (along < 0) return 'reactants';
  if (along > 1) return 'products';
  return offset.x * normal.x + offset.y * normal.y >= 0 ? 'above' : 'below';
}

export function centreOf(rect: Rect): Point {
  return { x: (rect.minX + rect.maxX) / 2, y: (rect.minY + rect.maxY) / 2 };
}

/** Assigns every molecule to a zone and sizes the four zones: a zone with molecules wraps them;
 *  an empty one is a placeholder square beside the arrow. */
export function reactionLayout(
  components: readonly MoleculeComponent[],
  arrow: ReactionArrowGeometry,
  options: ReactionLayoutOptions
): ReactionLayout {
  const molecules = components.map((component) => ({
    ...component,
    zone: zoneOf(centreOf(component.bounds), arrow.from, arrow.to)
  }));
  const { axis, normal, length } = arrowFrame(arrow.from, arrow.to);
  const half = Math.max(options.minPlaceholder, length * 0.35);
  const reach = options.gap + half;
  const middle = { x: (arrow.from.x + arrow.to.x) / 2, y: (arrow.from.y + arrow.to.y) / 2 };
  const placeholderCentre: Record<ReactionZone, Point> = {
    reactants: { x: arrow.from.x - axis.x * reach, y: arrow.from.y - axis.y * reach },
    products: { x: arrow.to.x + axis.x * reach, y: arrow.to.y + axis.y * reach },
    above: { x: middle.x + normal.x * reach, y: middle.y + normal.y * reach },
    below: { x: middle.x - normal.x * reach, y: middle.y - normal.y * reach }
  };
  const zones = Object.fromEntries(
    REACTION_ZONES.map((zone) => {
      const members = molecules.filter((molecule) => molecule.zone === zone);
      if (members.length === 0) {
        const centre = placeholderCentre[zone];
        const rect = { minX: centre.x - half, minY: centre.y - half, maxX: centre.x + half, maxY: centre.y + half };
        return [zone, { rect, empty: true }];
      }
      const union = members.reduce((rect, member) => unionOf(rect, member.bounds), members[0]!.bounds);
      return [zone, { rect: inflate(union, options.padding), empty: false }];
    })
  ) as Record<ReactionZone, ReactionZoneBox>;
  return { arrowId: arrow.id, molecules, zones };
}

function boundsOf(points: readonly Point[]): Rect {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

function unionOf(a: Rect, b: Rect): Rect {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY)
  };
}

function inflate(rect: Rect, by: number): Rect {
  return { minX: rect.minX - by, minY: rect.minY - by, maxX: rect.maxX + by, maxY: rect.maxY + by };
}

// ---------------------------------------------------------------------------------------------
// Boxes as containers (owner, 2026-09-26): once the arrow is placed, each zone is a box with its
// own position and size. A molecule is in a box when its bounding-box centre is inside it; a
// molecule in no box is loose (not part of the reaction, though it can still be searched). A box
// is drawn grown to fit what is in it, but never shrinks below its own size.

export type ReactionBoxes = Readonly<Record<ReactionZone, Rect>>;

export interface ReactionBoxOptions extends ReactionLayoutOptions {
  /** Smallest box, so an empty or tiny one is still an easy target. */
  readonly minWidth: number;
  readonly minHeight: number;
}

/** The boxes for a freshly placed arrow: fitted around what the drawing already has on each side
 *  of the arrow (see reactionLayout), each at least the minimum size, keeping its centre. */
export function defaultBoxes(
  components: readonly MoleculeComponent[],
  arrow: ReactionArrowGeometry,
  options: ReactionBoxOptions
): ReactionBoxes {
  const { zones } = reactionLayout(components, arrow, options);
  return Object.fromEntries(
    REACTION_ZONES.map((zone) => {
      const rect = zones[zone].rect;
      const centre = centreOf(rect);
      const halfWidth = Math.max(options.minWidth, rect.maxX - rect.minX) / 2;
      const halfHeight = Math.max(options.minHeight, rect.maxY - rect.minY) / 2;
      return [
        zone,
        {
          minX: centre.x - halfWidth,
          minY: centre.y - halfHeight,
          maxX: centre.x + halfWidth,
          maxY: centre.y + halfHeight
        }
      ];
    })
  ) as Record<ReactionZone, Rect>;
}

export interface BoxedMolecule extends MoleculeComponent {
  /** The box it is in, or null when it is loose. */
  readonly zone: ReactionZone | null;
}

/** #739 -- the zones whose molecules are joined by "+" (agents above/below the arrow are not). */
export const PLUS_JOINED_ZONES: readonly ReactionZone[] = ['reactants', 'products'];

/**
 * #739 -- where the "+" signs go between a box's molecules: one between each pair of neighbours.
 * Neighbours are read along the axis the molecules are spread out on (left to right for a row,
 * top to bottom for a stack), and the sign sits in the middle of the gap between their bounds on
 * that axis, centred on the other. Only molecules that overlap on both axes fall back to the point
 * between their centres. Derived from the layout every time, never stored, so adding, moving or
 * removing a molecule updates it.
 */
export function plusSignPositions(molecules: readonly MoleculeComponent[]): Point[] {
  if (molecules.length < 2) return [];
  const centres = molecules.map((molecule) => centreOf(molecule.bounds));
  const spread = (axis: 'x' | 'y') =>
    Math.max(...centres.map((centre) => centre[axis])) - Math.min(...centres.map((centre) => centre[axis]));
  const axis: 'x' | 'y' = spread('x') >= spread('y') ? 'x' : 'y';
  const ordered = [...molecules].sort((a, b) => centreOf(a.bounds)[axis] - centreOf(b.bounds)[axis]);
  const positions: Point[] = [];
  for (let index = 1; index < ordered.length; index++) {
    const first = ordered[index - 1]!.bounds;
    const second = ordered[index]!.bounds;
    const a = centreOf(first);
    const b = centreOf(second);
    const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    if (first.maxX < second.minX && axis === 'x') positions.push({ x: (first.maxX + second.minX) / 2, y: middle.y });
    else if (first.maxY < second.minY && axis === 'y')
      positions.push({ x: middle.x, y: (first.maxY + second.minY) / 2 });
    else if (first.maxX < second.minX) positions.push({ x: (first.maxX + second.minX) / 2, y: middle.y });
    else if (first.maxY < second.minY) positions.push({ x: middle.x, y: (first.maxY + second.minY) / 2 });
    else positions.push(middle);
  }
  return positions;
}

/** Which box each molecule is in: the first (in zone order) whose rectangle holds its centre. */
export function boxMembership(components: readonly MoleculeComponent[], boxes: ReactionBoxes): BoxedMolecule[] {
  return components.map((component) => {
    const centre = centreOf(component.bounds);
    const zone =
      REACTION_ZONES.find((candidate) => {
        const box = boxes[candidate];
        return centre.x >= box.minX && centre.x <= box.maxX && centre.y >= box.minY && centre.y <= box.maxY;
      }) ?? null;
    return { ...component, zone };
  });
}

/** Each box as drawn: its own rectangle, grown (never shrunk) to wrap its molecules plus padding. */
export function displayedBoxes(
  boxes: ReactionBoxes,
  molecules: readonly BoxedMolecule[],
  padding: number
): Readonly<Record<ReactionZone, ReactionZoneBox>> {
  return Object.fromEntries(
    REACTION_ZONES.map((zone) => {
      const members = molecules.filter((molecule) => molecule.zone === zone);
      const rect = members.reduce((grown, member) => unionOf(grown, inflate(member.bounds, padding)), boxes[zone]);
      return [zone, { rect, empty: members.length === 0 }];
    })
  ) as Record<ReactionZone, ReactionZoneBox>;
}

/**
 * #1134 -- which box each molecule is in, by the boxes as drawn: a box is drawn grown around its
 * members (displayedBoxes), so a molecule dropped into the grown part is in it too -- it looked
 * inside, yet counted as loose and was left out of the saved reaction. Members stay where they
 * are; a loose molecule joins the first box (zone order) whose drawn rectangle holds its centre,
 * which may grow that box in turn, until nothing changes. Returns the members and the boxes drawn.
 */
export function boxMembershipAsDrawn(
  components: readonly MoleculeComponent[],
  boxes: ReactionBoxes,
  padding: number
): {
  readonly molecules: readonly BoxedMolecule[];
  readonly shown: Readonly<Record<ReactionZone, ReactionZoneBox>>;
} {
  let molecules = boxMembership(components, boxes);
  let shown = displayedBoxes(boxes, molecules, padding);
  for (;;) {
    let joined = false;
    molecules = molecules.map((molecule) => {
      if (molecule.zone !== null) return molecule;
      const centre = centreOf(molecule.bounds);
      const zone =
        REACTION_ZONES.find((candidate) => {
          const { rect } = shown[candidate];
          return centre.x >= rect.minX && centre.x <= rect.maxX && centre.y >= rect.minY && centre.y <= rect.maxY;
        }) ?? null;
      if (zone === null) return molecule;
      joined = true;
      return { ...molecule, zone };
    });
    if (!joined) return { molecules, shown };
    shown = displayedBoxes(boxes, molecules, padding);
  }
}

export function translateRect(rect: Rect, dx: number, dy: number): Rect {
  return { minX: rect.minX + dx, minY: rect.minY + dy, maxX: rect.maxX + dx, maxY: rect.maxY + dy };
}

/**
 * #700 -- where to centre a structure added to a box ("Add to paper → box"): the box's centre when
 * it is empty, else beside what is already in it, on the side away from the arrow -- so the box
 * grows outwards and never across the arrow. `extent` is the structure's size, `gap` the room kept
 * between molecules (all in document units).
 */
export function placeInBox(
  box: Rect,
  members: readonly Rect[],
  arrow: ReactionArrowGeometry,
  extent: { readonly width: number; readonly height: number },
  gap: number
): Point {
  if (members.length === 0) return centreOf(box);
  const occupied = members.reduce(unionOf);
  const centre = centreOf(occupied);
  const away = { x: centre.x - (arrow.from.x + arrow.to.x) / 2, y: centre.y - (arrow.from.y + arrow.to.y) / 2 };
  if (Math.abs(away.x) >= Math.abs(away.y)) {
    return {
      x: away.x < 0 ? occupied.minX - gap - extent.width / 2 : occupied.maxX + gap + extent.width / 2,
      y: centre.y
    };
  }
  return {
    x: centre.x,
    y: away.y < 0 ? occupied.minY - gap - extent.height / 2 : occupied.maxY + gap + extent.height / 2
  };
}

/** #700 -- a box grown to hold `bounds` (with `padding` around it), never smaller than it was. */
export function growBox(box: Rect, bounds: Rect, padding: number): Rect {
  return unionOf(box, inflate(bounds, padding));
}
